/**
 * SidebarOnboardingItem — sidebar entry that appears only when the user
 * has incomplete business context. Pulses to draw attention and routes
 * to /onboarding. Hides automatically at 100%.
 *
 * Story Onboarding-3.
 */

import { NavLink, useLocation } from "react-router-dom";
import { Sparkle } from "@phosphor-icons/react";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useUIStore } from "@/stores/uiStore";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface Props {
  collapsed?: boolean;
  isDrawer?: boolean;
  onNavigate?: () => void;
}

export function SidebarOnboardingItem({ collapsed = false, isDrawer = false, onNavigate }: Props) {
  const { user } = useCurrentUser();
  const location = useLocation();
  const { query, completenessPct, isComplete } = useBusinessContext();

  if (!user) return null;
  if (query.isLoading || !query.isFetched) return null;
  if (isComplete) return null;

  const active = location.pathname.startsWith("/onboarding");

  const content = (
    <NavLink
      to="/onboarding"
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      aria-label={collapsed ? `Completar perfil — ${completenessPct}%` : undefined}
      className={cn(
        "group relative flex items-center gap-3 rounded-lg px-3 overflow-hidden",
        isDrawer ? "h-12" : "h-12 lg:h-10",
        "border border-primary/30 bg-primary/[0.06] text-foreground",
        "hover:bg-primary/[0.12] transition-colors",
      )}
    >
      <span aria-hidden className="relative flex items-center justify-center">
        <Sparkle weight="fill" className="size-5 text-primary" />
        <span className="absolute inset-0 -m-1 rounded-full bg-primary/30 motion-safe:animate-ping pointer-events-none" />
      </span>
      {!collapsed && (
        <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
          <span className="text-sm font-medium truncate">Completar perfil</span>
          <span className="text-xs font-semibold text-primary tabular-nums">
            {completenessPct}%
          </span>
        </div>
      )}
    </NavLink>
  );

  if (collapsed && !isDrawer) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="bg-card border-border">
          Completar perfil — {completenessPct}%
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

// Re-export sidebar collapsed state for convenience.
export function useSidebarCollapsed(): boolean {
  return useUIStore((s) => s.sidebarCollapsed);
}
